import { request, requestFormData } from './client';
import type {
  User,
  Novel,
  Chapter,
  ParseResponse,
  UploadNovelRequest,
  UploadNovelResponse,
  ExternalSearchResponse,
  SearchableSource,
  LLMProviderSettings,
  LLMProviderFormData,
  LLMTestResult,
  JailbreakPreset,
  JailbreakPresetListItem,
  JailbreakPresetFormData,
  JailbreakDefaults,
  JailbreakTestRequest,
  JailbreakTestResult,
  GenerateFakeConversationRequest,
  GenerateFakeConversationResult,
} from '../types';

// User management
export const adminUserApi = {
  /**
   * Get all users
   */
  list(): Promise<User[]> {
    return request<User[]>('/admin/users');
  },

  /**
   * Get pending users
   */
  getPending(): Promise<User[]> {
    return request<User[]>('/admin/users/pending');
  },

  /**
   * Approve a user
   */
  approve(userId: string): Promise<User> {
    return request<User>(`/admin/users/${userId}/approve`, { method: 'POST' });
  },

  /**
   * Reject a user
   */
  reject(userId: string): Promise<{ rejected: boolean }> {
    return request<{ rejected: boolean }>(`/admin/users/${userId}/reject`, { method: 'POST' });
  },

  /**
   * Unreject a user
   */
  unreject(userId: string): Promise<User> {
    return request<User>(`/admin/users/${userId}/unreject`, { method: 'POST' });
  },

  /**
   * Promote user to admin
   */
  promote(userId: string): Promise<User> {
    return request<User>(`/admin/users/${userId}/promote`, { method: 'POST' });
  },
};

// Novel management
export const adminNovelApi = {
  /**
   * Parse novel files for upload
   */
  parseFiles(files: File[]): Promise<ParseResponse> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files[]', file));
    return requestFormData<ParseResponse>('/admin/novels/parse', formData);
  },

  /**
   * Upload a novel with chapters
   */
  upload(data: UploadNovelRequest): Promise<UploadNovelResponse> {
    return request<UploadNovelResponse>('/admin/novels/upload', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Create novel only (for batch upload)
   */
  createOnly(data: {
    title: string;
    author?: string;
    description?: string;
    totalChapters?: number;
  }): Promise<Novel> {
    return request<Novel>('/admin/novels/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Upload a single chapter
   */
  uploadChapter(
    novelId: string,
    data: { index: number; title: string; content: string }
  ): Promise<{ chapterId: string; index: number; title: string; wordCount: number }> {
    return request<{ chapterId: string; index: number; title: string; wordCount: number }>(
      `/admin/novels/${novelId}/chapter`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Complete novel upload
   */
  completeUpload(novelId: string): Promise<Novel> {
    return request<Novel>(`/admin/novels/${novelId}/complete`, { method: 'POST' });
  },

  /**
   * Update novel metadata
   */
  update(
    id: string,
    data: { title?: string; author?: string; description?: string; cover_url?: string }
  ): Promise<Novel> {
    return request<Novel>(`/admin/novels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Add chapter to novel
   */
  addChapter(
    novelId: string,
    data: { title: string; content: string; index?: number }
  ): Promise<Chapter> {
    return request<Chapter>(`/admin/novels/${novelId}/chapters`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update chapter
   */
  updateChapter(id: string, data: { title: string }): Promise<Chapter> {
    return request<Chapter>(`/admin/chapters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete chapter
   */
  deleteChapter(id: string): Promise<void> {
    return request<void>(`/admin/chapters/${id}`, { method: 'DELETE' });
  },

  /**
   * Batch delete chapters
   */
  batchDeleteChapters(
    novelId: string,
    chapterIds: string[]
  ): Promise<{ deleted: boolean; deletedCount: number }> {
    return request<{ deleted: boolean; deletedCount: number }>(
      `/admin/novels/${novelId}/chapters/batch`,
      {
        method: 'DELETE',
        body: JSON.stringify({ chapterIds }),
      }
    );
  },

  /**
   * Reorder chapters
   */
  reorderChapters(
    novelId: string,
    chapters: Array<{ id: string; newIndex: number }>
  ): Promise<{ reordered: boolean; chapters: Chapter[] }> {
    return request<{ reordered: boolean; chapters: Chapter[] }>(
      `/admin/novels/${novelId}/reorder`,
      {
        method: 'POST',
        body: JSON.stringify({ chapters }),
      }
    );
  },

  /**
   * Get chapters for admin management
   */
  getChapters(novelId: string): Promise<{ novel: Novel; chapters: Chapter[] }> {
    return request<{ novel: Novel; chapters: Chapter[] }>(`/admin/novels/${novelId}/chapters`);
  },
};

// External search
export const adminSearchApi = {
  /**
   * Search external sources
   */
  search(query: string, limit: number = 20): Promise<ExternalSearchResponse> {
    return request<ExternalSearchResponse>(
      `/admin/search?q=${encodeURIComponent(query)}&limit=${limit}`
    );
  },

  /**
   * Get searchable sources
   */
  getSources(): Promise<{ sources: SearchableSource[]; total: number }> {
    return request<{ sources: SearchableSource[]; total: number }>('/admin/search/sources');
  },
};

// LLM Provider management
export const adminLLMApi = {
  /**
   * Get all LLM providers
   */
  list(): Promise<LLMProviderSettings[]> {
    return request<{ providers: LLMProviderSettings[] }>('/admin/llm/providers').then(
      (r) => r.providers
    );
  },

  /**
   * Get active LLM provider
   */
  async getActive(): Promise<LLMProviderSettings | null> {
    try {
      const result = await request<{ provider: LLMProviderSettings }>('/admin/llm/active');
      return result.provider;
    } catch {
      return null;
    }
  },

  /**
   * Create LLM provider
   */
  create(data: LLMProviderFormData): Promise<LLMProviderSettings> {
    return request<{ provider: LLMProviderSettings }>('/admin/llm/providers', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then((r) => r.provider);
  },

  /**
   * Update LLM provider
   */
  update(id: string, data: Partial<LLMProviderFormData>): Promise<LLMProviderSettings> {
    return request<{ provider: LLMProviderSettings }>(`/admin/llm/providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }).then((r) => r.provider);
  },

  /**
   * Delete LLM provider
   */
  delete(id: string): Promise<{ deleted: boolean }> {
    return request<{ deleted: boolean }>(`/admin/llm/providers/${id}`, { method: 'DELETE' });
  },

  /**
   * Activate LLM provider
   */
  activate(id: string): Promise<{ activated: boolean; provider_id: string }> {
    return request<{ activated: boolean; provider_id: string }>(
      `/admin/llm/providers/${id}/activate`,
      { method: 'POST' }
    );
  },

  /**
   * Enable LLM provider
   */
  enable(id: string): Promise<{ enabled: boolean; provider_id: string }> {
    return request<{ enabled: boolean; provider_id: string }>(
      `/admin/llm/providers/${id}/enable`,
      { method: 'PUT' }
    );
  },

  /**
   * Disable LLM provider
   */
  disable(id: string): Promise<{ disabled: boolean; provider_id: string }> {
    return request<{ disabled: boolean; provider_id: string }>(
      `/admin/llm/providers/${id}/disable`,
      { method: 'PUT' }
    );
  },

  /**
   * Get enabled LLM providers
   */
  getEnabled(): Promise<LLMProviderSettings[]> {
    return request<{ providers: LLMProviderSettings[] }>('/admin/llm/providers/enabled').then(
      (r) => r.providers
    );
  },

  /**
   * Test LLM provider
   */
  test(id: string): Promise<LLMTestResult> {
    return request<LLMTestResult>(`/admin/llm/providers/${id}/test`, { method: 'POST' });
  },

  // ============================================
  // Jailbreak Preset Management
  // ============================================

  /**
   * Get Jailbreak defaults (from code constants)
   */
  getJailbreakDefaults(): Promise<JailbreakDefaults> {
    return request<JailbreakDefaults>('/admin/llm/jailbreak/defaults');
  },

  /**
   * List all Jailbreak presets
   */
  listJailbreakPresets(): Promise<JailbreakPresetListItem[]> {
    return request<{ presets: JailbreakPresetListItem[] }>('/admin/llm/jailbreak/presets').then(
      (r) => r.presets
    );
  },

  /**
   * Get a single Jailbreak preset
   */
  getJailbreakPreset(id: string): Promise<JailbreakPreset> {
    return request<JailbreakPreset>(`/admin/llm/jailbreak/presets/${id}`);
  },

  /**
   * Create a new Jailbreak preset
   */
  createJailbreakPreset(data: JailbreakPresetFormData): Promise<JailbreakPreset> {
    return request<{ preset: JailbreakPreset }>('/admin/llm/jailbreak/presets', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then((r) => r.preset);
  },

  /**
   * Update a Jailbreak preset
   */
  updateJailbreakPreset(
    id: string,
    data: Partial<JailbreakPresetFormData>
  ): Promise<JailbreakPreset> {
    return request<{ preset: JailbreakPreset }>(`/admin/llm/jailbreak/presets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }).then((r) => r.preset);
  },

  /**
   * Delete a Jailbreak preset
   */
  deleteJailbreakPreset(id: string): Promise<{ deleted: boolean }> {
    return request<{ deleted: boolean }>(`/admin/llm/jailbreak/presets/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Generate Fake Conversation based on System Prompt
   */
  generateFakeConversation(
    data: GenerateFakeConversationRequest
  ): Promise<GenerateFakeConversationResult> {
    return request<GenerateFakeConversationResult>('/admin/llm/jailbreak/generate-conversation', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Execute Jailbreak test
   */
  testJailbreak(data: JailbreakTestRequest): Promise<JailbreakTestResult> {
    return request<JailbreakTestResult>('/admin/llm/jailbreak/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
